// react
import { useCallback, useMemo, useState } from 'react';

// mui
import {
	alpha,
	Alert,
	Box,
	Chip,
	CircularProgress,
	Collapse,
	Container,
	Divider,
	Grid,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// layouts
import { Authenticated } from '../../layouts/Authenticated/Authenticated';

// Types

interface BlockchainStatusData {
	schemaExists: boolean;
	message?: string;
	nodeStatus?: {
		connected: boolean;
		chainId?: number;
		blockNumber?: number;
		gasPrice?: string;
		rpcUrl?: string;
		signerAddress?: string;
		signerBalance?: string;
		error?: string;
	};
	contracts?: {
		name: string;
		address: string | null;
		deployed: boolean;
		codeSize?: number;
		implAddress?: string;
		version?: string;
	}[];
	tables?: Record<string, number>;
	totalRows?: number;
	financialSummary?: {
		total_balance: string;
		total_locked: string;
		total_available: string;
		total_disbursed: string;
		total_allocated: string;
		total_refunded: string;
		active_accounts: number;
		enforcing_envelopes: number;
		planning_envelopes: number;
	};
	paymentBreakdown?: { tx_type: string; status: string; count: number; total_amount: string }[];
	accountTxBreakdown?: { tx_type: string; count: number; total_amount: string }[];
	recentAccounts?: Record<string, unknown>[];
	recentEnvelopes?: Record<string, unknown>[];
	recentPayments?: Record<string, unknown>[];
	recentVouchers?: Record<string, unknown>[];
	recentAccountTxns?: Record<string, unknown>[];
	failures?: Record<string, unknown>[];
	timestamp?: string;
}

interface ActiveFilter {
	field: string;
	label: string;
	value: string;
	table: string;
}

// Helpers

const CHAIN_SCALE = 10000; // 4-decimal fixed-point: on-chain 150000 = human 15.0

/** Convert an on-chain integer amount to a human-readable number. */
const fromChain = (raw: string | number | bigint): number => {
	const n = typeof raw === 'string' ? Number(raw) : Number(raw);
	if (isNaN(n)) return 0;
	return n / CHAIN_SCALE;
};

/** Format a human-readable number with locale separators and up to 4 decimals. */
const fmtNum = (n: number): string =>
	n % 1 === 0 ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 4 });

const HASH_FIELDS = new Set([
	'owner',
	'recipient',
	'blockchain_tx_hash',
	'tx_hash',
	'transaction_hash',
	'mint_tx_hash',
	'redeem_tx_hash',
]);
const isHashField = (key: string): boolean => HASH_FIELDS.has(key);

/** Column keys whose values are on-chain amounts (need ÷ CHAIN_SCALE). */
const AMOUNT_FIELDS = new Set([
	'balance',
	'amount',
	'new_balance',
	'locked_amount',
	'total_allocated',
	'total_disbursed',
	'total_refunded',
	'available_balance',
	'total_amount',
]);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatCellValue = (value: any, colKey?: string): string => {
	if (value === null || value === undefined) return '\u2014';
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (typeof value === 'string' && value.startsWith('0x') && value.length > 20)
		return `${value.slice(0, 10)}\u2026${value.slice(-6)}`;
	if (colKey && AMOUNT_FIELDS.has(colKey)) return fmtNum(fromChain(value));
	if (typeof value === 'string' && !isNaN(Date.parse(value)) && value.includes('T'))
		return new Date(value).toLocaleString();
	if (typeof value === 'number' && value >= 1000) return value.toLocaleString();
	return String(value);
};

const formatAmount = (value: string | number | undefined): string => {
	if (!value) return '0';
	return fmtNum(fromChain(value));
};

const needsTooltip = (value: unknown): boolean =>
	typeof value === 'string' && value.startsWith('0x') && value.length > 20;

// Stat Card

const StatCard = ({
	icon,
	label,
	value,
	color = 'primary',
	active = false,
	onClick,
}: {
	icon: React.ReactNode;
	label: string;
	value: number | string;
	color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
	active?: boolean;
	onClick?: () => void;
}) => (
	<Paper
		onClick={onClick}
		elevation={active ? 4 : 0}
		sx={{
			p: 2.5,
			display: 'flex',
			alignItems: 'center',
			gap: 2,
			cursor: onClick ? 'pointer' : 'default',
			border: '1px solid',
			borderColor: active ? `${color}.main` : 'divider',
			borderRadius: 3,
			transition: 'all 0.2s ease',
			background: active ? (t: Theme) => alpha(t.palette[color].main, 0.06) : 'background.paper',
			'&:hover': onClick
				? {
						borderColor: `${color}.main`,
						boxShadow: (t: Theme) => `0 4px 20px ${alpha(t.palette[color].main, 0.15)}`,
						transform: 'translateY(-1px)',
					}
				: {},
		}}>
		<Box
			sx={{
				color: `${color}.main`,
				bgcolor: (t: Theme) => alpha(t.palette[color].main, 0.1),
				borderRadius: 2,
				p: 1,
				display: 'flex',
			}}>
			{icon}
		</Box>
		<Box>
			<Typography sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2 }}>
				{value}
			</Typography>
			<Typography sx={{ fontSize: '1.25rem', color: 'text.secondary', mt: 0.25 }}>
				{label}
			</Typography>
		</Box>
	</Paper>
);

// Data Table Cell — with Tooltip for truncated addresses

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CellContent = ({ value, colKey }: { value: any; colKey: string }) => {
	const display = formatCellValue(value, colKey);
	const showTooltip = needsTooltip(value);

	if (showTooltip) {
		return (
			<Tooltip
				title={
					<Typography
						sx={{
							fontSize: '0.85rem',
							fontFamily: '"Roboto Mono", monospace',
							wordBreak: 'break-all',
						}}>
						{String(value)}
					</Typography>
				}
				placement="top"
				arrow>
				<Box
					component="span"
					sx={{
						fontFamily: '"SF Mono", "Roboto Mono", monospace',
						cursor: 'help',
						borderBottom: '1px dashed',
						borderColor: 'grey.400',
						pb: '1px',
					}}>
					{display}
				</Box>
			</Tooltip>
		);
	}
	return <>{display}</>;
};

// Data Table

const DataTable = ({
	title,
	columns,
	rows,
	onCellClick,
	filterableKeys,
}: {
	title: string;
	columns: { key: string; label: string }[];
	rows: Record<string, unknown>[];
	onCellClick?: (key: string, value: unknown, label: string) => void;
	filterableKeys?: Set<string>;
}) => (
	<Paper
		elevation={0}
		sx={{
			border: '1px solid',
			borderColor: 'divider',
			borderRadius: 3,
			overflow: 'hidden',
		}}>
		<Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
			<Typography sx={{ fontSize: '1.35rem', fontWeight: 700 }}>{title}</Typography>
		</Box>
		{rows.length === 0 ? (
			<Box sx={{ px: 3, pb: 3 }}>
				<Typography sx={{ fontSize: '1.25rem', color: 'text.secondary' }}>No data yet</Typography>
			</Box>
		) : (
			<TableContainer sx={{ maxHeight: 460 }}>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							{columns.map((col) => (
								<TableCell
									key={col.key}
									sx={{
										fontWeight: 700,
										fontSize: '1.25rem',
										whiteSpace: 'nowrap',
										py: 1.5,
										bgcolor: 'grey.50',
										borderBottom: '2px solid',
										borderColor: 'divider',
									}}>
									{col.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row, i) => (
							<TableRow
								key={i}
								hover
								sx={{
									'&:last-child td': { borderBottom: 0 },
								}}>
								{columns.map((col) => {
									const isFilterable = filterableKeys?.has(col.key) && onCellClick;
									return (
										<TableCell
											key={col.key}
											onClick={
												isFilterable
													? () => onCellClick(col.key, row[col.key], col.label)
													: undefined
											}
											sx={{
												fontFamily: isHashField(col.key)
													? '"SF Mono", "Roboto Mono", monospace'
													: 'inherit',
												fontSize: '1.25rem',
												whiteSpace: 'nowrap',
												py: 1.75,
												cursor: isFilterable ? 'pointer' : 'default',
												'&:hover': isFilterable
													? {
															bgcolor: 'action.hover',
															borderRadius: 1,
														}
													: {},
											}}>
											<CellContent value={row[col.key]} colKey={col.key} />
										</TableCell>
									);
								})}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		)}
	</Paper>
);

// Active Filters Bar

const FilterBar = ({
	filters,
	onRemove,
	onClearAll,
}: {
	filters: ActiveFilter[];
	onRemove: (filter: ActiveFilter) => void;
	onClearAll: () => void;
}) => {
	if (filters.length === 0) return null;

	return (
		<Paper
			elevation={0}
			sx={{
				px: 2.5,
				py: 1.5,
				mb: 2.5,
				display: 'flex',
				alignItems: 'center',
				gap: 1.5,
				flexWrap: 'wrap',
				border: '1px solid',
				borderColor: 'primary.light',
				borderRadius: 3,
				bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
			}}>
			<Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: 'primary.main', mr: 0.5 }}>
				Filters:
			</Typography>
			{filters.map((f, i) => (
				<Chip
					key={`${f.field}-${f.value}-${i}`}
					label={`${f.label}: ${formatCellValue(f.value)}`}
					onDelete={() => onRemove(f)}
					deleteIcon={<CloseIcon sx={{ fontSize: '1.1rem !important' }} />}
					sx={{
						fontSize: '1.1rem',
						height: 36,
						borderRadius: 2,
						fontWeight: 500,
						bgcolor: 'primary.main',
						color: 'primary.contrastText',
						'& .MuiChip-deleteIcon': {
							color: 'primary.contrastText',
							opacity: 0.8,
							'&:hover': { opacity: 1 },
						},
					}}
				/>
			))}
			<Button
				size="small"
				startIcon={<FilterListOffIcon />}
				onClick={onClearAll}
				sx={{ ml: 'auto', fontSize: '1.1rem', textTransform: 'none' }}>
				Clear All
			</Button>
		</Paper>
	);
};

// Page: Blockchain Status

export const PageBlockchainStatus = () => {
	const [data, setData] = useState<BlockchainStatusData | null>(null);
	const [fetchError, setFetchError] = useState<string | null>(null);
	const [filters, setFilters] = useState<ActiveFilter[]>([]);
	const [nodeExpanded, setNodeExpanded] = useState(true);

	const { isLoading, refetch } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/blockchain-status`,
		onSuccess: (res) => {
			setData(res.data);
			setFetchError(null);
		},
		onError: (err) => {
			setFetchError(err?.message || 'Failed to fetch blockchain status');
		},
	});

	// --- Filter logic ---
	const addFilter = useCallback((key: string, value: unknown, label: string, table: string) => {
		if (value === null || value === undefined) return;
		const strValue = String(value);
		setFilters((prev) => {
			// Toggle off if same filter already active
			const existing = prev.findIndex(
				(f) => f.field === key && f.value === strValue && f.table === table
			);
			if (existing >= 0) return prev.filter((_, i) => i !== existing);
			// Replace filter for same field+table, or add new
			const without = prev.filter((f) => !(f.field === key && f.table === table));
			return [...without, { field: key, label, value: strValue, table }];
		});
	}, []);

	const removeFilter = useCallback((filter: ActiveFilter) => {
		setFilters((prev) =>
			prev.filter(
				(f) => !(f.field === filter.field && f.value === filter.value && f.table === filter.table)
			)
		);
	}, []);

	const clearFilters = useCallback(() => setFilters([]), []);

	// Apply filters to rows
	const applyFilters = useCallback(
		(rows: Record<string, unknown>[] | undefined, tableName: string): Record<string, unknown>[] => {
			if (!rows) return [];
			const tableFilters = filters.filter((f) => f.table === tableName);
			if (tableFilters.length === 0) return rows;
			return rows.filter((row) => tableFilters.every((f) => String(row[f.field]) === f.value));
		},
		[filters]
	);

	const filteredAccounts = useMemo(
		() => applyFilters(data?.recentAccounts, 'accounts'),
		[applyFilters, data?.recentAccounts]
	);
	const filteredEnvelopes = useMemo(
		() => applyFilters(data?.recentEnvelopes, 'envelopes'),
		[applyFilters, data?.recentEnvelopes]
	);
	const filteredPayments = useMemo(
		() => applyFilters(data?.recentPayments, 'payments'),
		[applyFilters, data?.recentPayments]
	);
	const filteredVouchers = useMemo(
		() => applyFilters(data?.recentVouchers, 'vouchers'),
		[applyFilters, data?.recentVouchers]
	);
	const filteredAccountTxns = useMemo(
		() => applyFilters(data?.recentAccountTxns, 'accountTxns'),
		[applyFilters, data?.recentAccountTxns]
	);

	return (
		<Authenticated>
			<Box sx={{ bgcolor: 'grey.50', minHeight: '100vh' }}>
				<Container maxWidth="xl" sx={{ py: 4 }}>
					{/* Header */}
					<Box
						sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
						<Box>
							<Typography sx={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.01em' }}>
								Blockchain V2 Status
							</Typography>
							<Typography sx={{ fontSize: '1.25rem', color: 'text.secondary', mt: 0.5 }}>
								Read model data from the blk_v2 schema
							</Typography>
						</Box>
						<Button
							variant="contained"
							disableElevation
							startIcon={<RefreshIcon />}
							onClick={refetch}
							disabled={isLoading}
							sx={{ fontSize: '1.1rem', textTransform: 'none', borderRadius: 2, px: 3, py: 1 }}>
							Refresh
						</Button>
					</Box>

					{/* Loading */}
					{isLoading && !data && (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
							<CircularProgress size={48} />
						</Box>
					)}

					{/* Error */}
					{fetchError && (
						<Alert severity="error" sx={{ mb: 2, fontSize: '1.25rem', borderRadius: 2 }}>
							{fetchError}
						</Alert>
					)}

					{/* Schema not found */}
					{data && !data.schemaExists && (
						<Alert severity="warning" sx={{ mb: 2, fontSize: '1.25rem', borderRadius: 2 }}>
							The blk_v2 schema does not exist in the database. Run the migration or{' '}
							<code>scripts/reset-database.sh</code> from bldt-contracts-v2.
						</Alert>
					)}

					{/* Data */}
					{data && data.schemaExists && (
						<>
							{/* Schema health */}
							<Paper
								elevation={0}
								sx={{
									px: 3,
									py: 2,
									mb: 3,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									border: '1px solid',
									borderColor: 'success.light',
									borderRadius: 3,
									bgcolor: (t) => alpha(t.palette.success.main, 0.06),
								}}>
								<Stack direction="row" alignItems="center" gap={1.5}>
									<CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.5rem' }} />
									<Typography sx={{ fontSize: '1.25rem', fontWeight: 500 }}>
										blk_v2 schema is active &mdash; <strong>{data.totalRows}</strong> total rows
										across <strong>{Object.keys(data.tables || {}).length}</strong> tables
									</Typography>
								</Stack>
								{data.timestamp && (
									<Chip
										label={`Updated: ${new Date(data.timestamp).toLocaleTimeString()}`}
										sx={{ fontSize: '1rem', height: 32, borderRadius: 2 }}
									/>
								)}
							</Paper>

							{/* ── Blockchain Node Panel ── */}
							{(data.nodeStatus || (data.contracts && data.contracts.length > 0)) && (
								<Paper
									elevation={0}
									sx={{
										mb: 3,
										border: '1px solid',
										borderColor: 'divider',
										borderRadius: 3,
										overflow: 'hidden',
									}}>
									{/* Header bar — always visible, clickable to toggle */}
									{data.nodeStatus && (
										<Box
											onClick={() => setNodeExpanded((prev) => !prev)}
											sx={{
												px: 3,
												py: 2,
												cursor: 'pointer',
												userSelect: 'none',
												'&:hover': { bgcolor: 'action.hover' },
											}}>
											<Stack direction="row" alignItems="center" justifyContent="space-between">
												<Stack direction="row" alignItems="center" gap={1.5}>
													{data.nodeStatus.connected ? (
														<LinkIcon sx={{ color: 'info.main', fontSize: '1.4rem' }} />
													) : (
														<LinkOffIcon sx={{ color: 'warning.main', fontSize: '1.4rem' }} />
													)}
													<Typography sx={{ fontSize: '1.15rem', fontWeight: 600 }}>
														{data.nodeStatus.connected ? 'Blockchain Node' : 'Node Disconnected'}
													</Typography>
													{data.nodeStatus.connected && (
														<Chip
															label="Live"
															size="small"
															color="success"
															sx={{
																fontSize: '0.8rem',
																height: 22,
																fontWeight: 600,
																'& .MuiChip-label': { px: 1 },
															}}
														/>
													)}
													{!nodeExpanded && data.nodeStatus.connected && (
														<Typography
															sx={{
																fontSize: '0.9rem',
																color: 'text.secondary',
																ml: 1,
															}}>
															Chain {data.nodeStatus.chainId} &middot; Block{' '}
															{data.nodeStatus.blockNumber?.toLocaleString()} &middot;{' '}
															{data.contracts?.filter((c) => c.deployed).length || 0}/
															{data.contracts?.length || 0} contracts
														</Typography>
													)}
												</Stack>
												<Stack direction="row" alignItems="center" gap={1}>
													{data.nodeStatus.rpcUrl && (
														<Chip
															label={data.nodeStatus.rpcUrl}
															size="small"
															variant="outlined"
															sx={{
																fontSize: '0.9rem',
																height: 26,
																borderRadius: 1.5,
																fontFamily: 'monospace',
															}}
														/>
													)}
													<ExpandMoreIcon
														sx={{
															color: 'text.secondary',
															transform: nodeExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
															transition: 'transform 0.2s',
														}}
													/>
												</Stack>
											</Stack>
										</Box>
									)}

									<Collapse in={nodeExpanded}>
										{data.nodeStatus?.connected && (
											<Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
												<Grid container spacing={2}>
													<Grid item xs={6} sm={3}>
														<Typography
															variant="caption"
															sx={{
																color: 'text.secondary',
																fontSize: '0.8rem',
																textTransform: 'uppercase',
																letterSpacing: 0.5,
															}}>
															Chain ID
														</Typography>
														<Typography
															sx={{ fontSize: '1.2rem', fontWeight: 700, fontFamily: 'monospace' }}>
															{data.nodeStatus.chainId}
														</Typography>
													</Grid>
													<Grid item xs={6} sm={3}>
														<Typography
															variant="caption"
															sx={{
																color: 'text.secondary',
																fontSize: '0.8rem',
																textTransform: 'uppercase',
																letterSpacing: 0.5,
															}}>
															Block
														</Typography>
														<Typography
															sx={{ fontSize: '1.2rem', fontWeight: 700, fontFamily: 'monospace' }}>
															{data.nodeStatus.blockNumber?.toLocaleString()}
														</Typography>
													</Grid>
													<Grid item xs={6} sm={3}>
														<Typography
															variant="caption"
															sx={{
																color: 'text.secondary',
																fontSize: '0.8rem',
																textTransform: 'uppercase',
																letterSpacing: 0.5,
															}}>
															Gas Price
														</Typography>
														<Typography
															sx={{ fontSize: '1.2rem', fontWeight: 700, fontFamily: 'monospace' }}>
															{data.nodeStatus.gasPrice
																? `${(Number(data.nodeStatus.gasPrice) / 1e9).toFixed(1)} gwei`
																: '—'}
														</Typography>
													</Grid>
													{data.nodeStatus.signerBalance && (
														<Grid item xs={6} sm={3}>
															<Typography
																variant="caption"
																sx={{
																	color: 'text.secondary',
																	fontSize: '0.8rem',
																	textTransform: 'uppercase',
																	letterSpacing: 0.5,
																}}>
																Signer Balance
															</Typography>
															<Typography
																sx={{
																	fontSize: '1.2rem',
																	fontWeight: 700,
																	fontFamily: 'monospace',
																}}>
																{parseFloat(data.nodeStatus.signerBalance).toLocaleString()} ETH
															</Typography>
														</Grid>
													)}
												</Grid>
												{data.nodeStatus.signerAddress && (
													<Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1.5 }}>
														<AccountBalanceWalletIcon
															sx={{ fontSize: '1rem', color: 'text.secondary' }}
														/>
														<Typography
															sx={{
																fontSize: '0.85rem',
																fontFamily: 'monospace',
																color: 'text.secondary',
															}}>
															Signer: {data.nodeStatus.signerAddress}
														</Typography>
													</Stack>
												)}
											</Box>
										)}

										{/* Disconnected error */}
										{data.nodeStatus && !data.nodeStatus.connected && data.nodeStatus.error && (
											<Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
												<Typography sx={{ fontSize: '0.95rem', color: 'warning.dark' }}>
													{data.nodeStatus.error}
												</Typography>
											</Box>
										)}

										{/* Contracts */}
										{data.contracts && data.contracts.length > 0 && (
											<Box sx={{ px: 3, py: 2 }}>
												<Typography
													sx={{
														fontSize: '0.8rem',
														fontWeight: 600,
														textTransform: 'uppercase',
														letterSpacing: 0.5,
														color: 'text.secondary',
														mb: 1.5,
													}}>
													Smart Contracts
												</Typography>
												<Stack spacing={1}>
													{data.contracts.map((c) => (
														<Box
															key={c.name}
															sx={{
																px: 2,
																py: 1.5,
																borderRadius: 2,
																bgcolor: 'grey.50',
																border: '1px solid',
																borderColor: c.deployed
																	? 'transparent'
																	: (t) => alpha(t.palette.error.main, 0.2),
															}}>
															<Stack
																direction="row"
																alignItems="center"
																justifyContent="space-between">
																<Stack direction="row" alignItems="center" gap={1.5}>
																	{c.deployed ? (
																		<CheckCircleIcon
																			sx={{
																				color: 'success.main',
																				fontSize: '1.1rem',
																			}}
																		/>
																	) : (
																		<ErrorIcon sx={{ color: 'error.main', fontSize: '1.1rem' }} />
																	)}
																	<Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
																		{c.name === 'account'
																			? 'Account_V1'
																			: c.name === 'envelope'
																				? 'Envelope_V1'
																				: 'VoucherNFT_V1'}
																	</Typography>
																	{c.version && (
																		<Chip
																			label={`v${c.version}`}
																			size="small"
																			color="success"
																			variant="outlined"
																			sx={{
																				fontSize: '0.8rem',
																				height: 22,
																				'& .MuiChip-label': { px: 0.75 },
																			}}
																		/>
																	)}
																	{c.codeSize != null && (
																		<Chip
																			label={`${(c.codeSize / 1024).toFixed(1)} KB`}
																			size="small"
																			variant="outlined"
																			sx={{
																				fontSize: '0.8rem',
																				height: 22,
																				'& .MuiChip-label': { px: 0.75 },
																			}}
																		/>
																	)}
																</Stack>
																<Typography
																	sx={{
																		fontSize: '0.85rem',
																		fontFamily: 'monospace',
																		color: c.address ? 'text.primary' : 'text.disabled',
																	}}>
																	{c.address || 'Not configured'}
																</Typography>
															</Stack>
															{c.implAddress && (
																<Typography
																	sx={{
																		fontSize: '0.8rem',
																		fontFamily: 'monospace',
																		color: 'text.disabled',
																		mt: 0.5,
																		ml: 4,
																	}}>
																	→ impl: {c.implAddress}
																</Typography>
															)}
														</Box>
													))}
												</Stack>
											</Box>
										)}
									</Collapse>
								</Paper>
							)}

							{/* Active Filters */}
							<FilterBar filters={filters} onRemove={removeFilter} onClearAll={clearFilters} />

							{/* Stat cards — click to filter */}
							<Grid container spacing={2.5} sx={{ mb: 3 }}>
								<Grid item xs={6} sm={3}>
									<StatCard
										icon={<AccountBalanceWalletIcon sx={{ fontSize: 28 }} />}
										label="Accounts"
										value={data.tables?.accounts ?? 0}
										color="primary"
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<StatCard
										icon={<StorageIcon sx={{ fontSize: 28 }} />}
										label="Envelopes"
										value={data.tables?.envelopes ?? 0}
										color="info"
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<StatCard
										icon={<PaymentIcon sx={{ fontSize: 28 }} />}
										label="Payments"
										value={data.tables?.payments ?? 0}
										color="success"
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<StatCard
										icon={<ConfirmationNumberIcon sx={{ fontSize: 28 }} />}
										label="Vouchers"
										value={data.tables?.vouchers ?? 0}
										color="secondary"
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<StatCard
										icon={<ReceiptLongIcon sx={{ fontSize: 28 }} />}
										label="Account Txns"
										value={data.tables?.account_transactions ?? 0}
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<StatCard
										icon={<ReceiptLongIcon sx={{ fontSize: 28 }} />}
										label="Envelope Transfers"
										value={data.tables?.envelope_transfers ?? 0}
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<StatCard
										icon={<ReceiptLongIcon sx={{ fontSize: 28 }} />}
										label="Standalone Payments"
										value={data.tables?.standalone_payments ?? 0}
									/>
								</Grid>
								<Grid item xs={6} sm={3}>
									<StatCard
										icon={<ErrorIcon sx={{ fontSize: 28 }} />}
										label="Failed Operations"
										value={data.tables?.blockchain_failures ?? 0}
										color={data.tables?.blockchain_failures ? 'error' : 'success'}
									/>
								</Grid>
							</Grid>

							{/* Fund Position & Activity */}
							{data.financialSummary && (
								<Grid container spacing={2.5} sx={{ mb: 3 }}>
									{/* Position */}
									<Grid item xs={12} md={5}>
										<Paper
											elevation={0}
											sx={{
												p: 3,
												border: '1px solid',
												borderColor: 'divider',
												borderRadius: 3,
												height: '100%',
											}}>
											<Stack direction="row" alignItems="center" gap={1} sx={{ mb: 3 }}>
												<AttachMoneyIcon sx={{ color: 'primary.main', fontSize: 24 }} />
												<Typography sx={{ fontSize: '1.35rem', fontWeight: 700 }}>
													Position
												</Typography>
											</Stack>

											{(() => {
												const balance = fromChain(data.financialSummary.total_balance);
												const locked = fromChain(data.financialSummary.total_locked);
												const unlocked = balance - locked;
												const lockedPct = balance > 0 ? (locked / balance) * 100 : 0;
												const allocated = fromChain(data.financialSummary.total_allocated);
												const disbursed = fromChain(data.financialSummary.total_disbursed);

												return (
													<>
														{/* Hero balance */}
														<Box sx={{ textAlign: 'center', mb: 2.5 }}>
															<Typography
																sx={{
																	fontSize: '0.8rem',
																	color: 'text.secondary',
																	textTransform: 'uppercase',
																	letterSpacing: 0.5,
																	fontWeight: 600,
																	mb: 0.5,
																}}>
																Account Balance
															</Typography>
															<Typography
																sx={{
																	fontSize: '2.5rem',
																	fontWeight: 800,
																	fontFamily: '"Roboto Mono", monospace',
																	lineHeight: 1,
																}}>
																{formatAmount(data.financialSummary.total_balance)}
															</Typography>
														</Box>

														{/* Bar */}
														<Box
															sx={{
																display: 'flex',
																height: 24,
																borderRadius: 12,
																overflow: 'hidden',
																mb: 2,
																border: '1px solid',
																borderColor: 'divider',
															}}>
															{lockedPct > 0 && (
																<Tooltip
																	title={`Locked: ${fmtNum(locked)} (${lockedPct.toFixed(0)}%)`}
																	arrow>
																	<Box
																		sx={{
																			width: `${lockedPct}%`,
																			bgcolor: 'primary.main',
																			minWidth: 8,
																		}}
																	/>
																</Tooltip>
															)}
															<Box sx={{ flex: 1, bgcolor: 'grey.200' }} />
														</Box>

														{/* Key figures */}
														<Stack spacing={1} sx={{ mb: 2 }}>
															{[
																{
																	label: 'Locked',
																	value: locked,
																	color: 'primary.main',
																	bold: true,
																},
																{
																	label: 'Unlocked',
																	value: unlocked,
																	color: 'text.secondary',
																	bold: false,
																},
																...(allocated > 0
																	? [
																			{
																				label: 'Allocated',
																				value: allocated,
																				color: 'text.secondary',
																				bold: false,
																			},
																		]
																	: []),
																...(disbursed > 0
																	? [
																			{
																				label: 'Disbursed',
																				value: disbursed,
																				color: 'text.secondary',
																				bold: false,
																			},
																		]
																	: []),
															].map((row) => (
																<Box
																	key={row.label}
																	sx={{
																		display: 'flex',
																		justifyContent: 'space-between',
																		alignItems: 'center',
																		px: 1.5,
																		py: 0.75,
																	}}>
																	<Stack direction="row" alignItems="center" gap={1}>
																		{row.bold && (
																			<Box
																				sx={{
																					width: 8,
																					height: 8,
																					borderRadius: '50%',
																					bgcolor: row.color,
																				}}
																			/>
																		)}
																		<Typography
																			sx={{
																				fontSize: '1rem',
																				fontWeight: row.bold ? 600 : 400,
																				color: row.bold ? 'text.primary' : 'text.secondary',
																			}}>
																			{row.label}
																		</Typography>
																	</Stack>
																	<Typography
																		sx={{
																			fontSize: '1.05rem',
																			fontWeight: row.bold ? 700 : 500,
																			fontFamily: '"Roboto Mono", monospace',
																			color: row.color,
																		}}>
																		{fmtNum(row.value)}
																	</Typography>
																</Box>
															))}
														</Stack>

														<Divider />
														<Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
															<Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
																<strong>{data.financialSummary.active_accounts}</strong> account
																{data.financialSummary.active_accounts !== 1 ? 's' : ''}
															</Typography>
															{data.financialSummary.enforcing_envelopes > 0 && (
																<Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
																	<strong>{data.financialSummary.enforcing_envelopes}</strong>{' '}
																	enforcing
																</Typography>
															)}
															{data.financialSummary.planning_envelopes > 0 && (
																<Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
																	<strong>{data.financialSummary.planning_envelopes}</strong>{' '}
																	planning
																</Typography>
															)}
														</Stack>
													</>
												);
											})()}
										</Paper>
									</Grid>

									{/* Activity Ledger */}
									<Grid item xs={12} md={7}>
										<Paper
											elevation={0}
											sx={{
												p: 3,
												border: '1px solid',
												borderColor: 'divider',
												borderRadius: 3,
												height: '100%',
											}}>
											<Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2.5 }}>
												<SwapHorizIcon sx={{ color: 'primary.main', fontSize: 24 }} />
												<Typography sx={{ fontSize: '1.35rem', fontWeight: 700 }}>
													Activity
												</Typography>
											</Stack>

											{(() => {
												const lockRow = data.paymentBreakdown?.find((r) => r.tx_type === 'LOCK');
												const refundRow = data.paymentBreakdown?.find(
													(r) => r.tx_type === 'REFUND'
												);
												const mintRow = data.accountTxBreakdown?.find((r) => r.tx_type === 'MINT');
												const burnRow = data.accountTxBreakdown?.find((r) => r.tx_type === 'BURN');

												type LedgerRow = {
													label: string;
													count: number;
													amount: number;
													sign: '+' | '−';
													chipColor: 'success' | 'error' | 'primary' | 'warning';
													category: string;
												};
												const rows: LedgerRow[] = [
													...(mintRow
														? [
																{
																	label: 'MINT',
																	count: mintRow.count,
																	amount: fromChain(mintRow.total_amount),
																	sign: '+' as const,
																	chipColor: 'success' as const,
																	category: 'Tokens',
																},
															]
														: []),
													...(lockRow
														? [
																{
																	label: 'LOCK',
																	count: lockRow.count,
																	amount: fromChain(lockRow.total_amount),
																	sign: '+' as const,
																	chipColor: 'primary' as const,
																	category: 'Envelopes',
																},
															]
														: []),
													...(refundRow
														? [
																{
																	label: 'REFUND',
																	count: refundRow.count,
																	amount: fromChain(refundRow.total_amount),
																	sign: '−' as const,
																	chipColor: 'warning' as const,
																	category: 'Envelopes',
																},
															]
														: []),
													...(burnRow
														? [
																{
																	label: 'BURN',
																	count: burnRow.count,
																	amount: fromChain(burnRow.total_amount),
																	sign: '−' as const,
																	chipColor: 'error' as const,
																	category: 'Tokens',
																},
															]
														: []),
												];

												const netSupply =
													(mintRow ? fromChain(mintRow.total_amount) : 0) -
													(burnRow ? fromChain(burnRow.total_amount) : 0);
												const netLocked =
													(lockRow ? fromChain(lockRow.total_amount) : 0) -
													(refundRow ? fromChain(refundRow.total_amount) : 0);

												if (rows.length === 0) {
													return (
														<Typography
															sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
															No activity yet
														</Typography>
													);
												}

												return (
													<>
														<Stack spacing={1}>
															{rows.map((row) => (
																<Box
																	key={row.label}
																	sx={{
																		display: 'flex',
																		justifyContent: 'space-between',
																		alignItems: 'center',
																		p: 1.25,
																		borderRadius: 2,
																		bgcolor: 'grey.50',
																		border: '1px solid',
																		borderColor: 'divider',
																	}}>
																	<Stack direction="row" alignItems="center" gap={1.5}>
																		<Chip
																			label={row.label}
																			size="small"
																			color={row.chipColor}
																			sx={{ fontSize: '0.85rem', fontWeight: 600, minWidth: 72 }}
																		/>
																		<Typography
																			sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
																			{row.count} txn{row.count !== 1 ? 's' : ''}
																		</Typography>
																	</Stack>
																	<Typography
																		sx={{
																			fontSize: '1.1rem',
																			fontWeight: 700,
																			fontFamily: '"Roboto Mono", monospace',
																			color: row.sign === '−' ? 'warning.dark' : 'text.primary',
																		}}>
																		{row.sign === '−' ? '−' : ''}
																		{fmtNum(row.amount)}
																	</Typography>
																</Box>
															))}
														</Stack>

														{/* Net totals */}
														<Box
															sx={{
																mt: 2,
																pt: 1.5,
																borderTop: '2px dashed',
																borderColor: 'divider',
															}}>
															<Stack spacing={0.5}>
																<Box
																	sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
																	<Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
																		Net Supply
																	</Typography>
																	<Typography
																		sx={{
																			fontSize: '1.1rem',
																			fontWeight: 700,
																			fontFamily: '"Roboto Mono", monospace',
																		}}>
																		{fmtNum(netSupply)}
																	</Typography>
																</Box>
																<Box
																	sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
																	<Typography
																		sx={{
																			fontSize: '1rem',
																			fontWeight: 600,
																			color: 'primary.main',
																		}}>
																		Net Locked
																	</Typography>
																	<Typography
																		sx={{
																			fontSize: '1.1rem',
																			fontWeight: 700,
																			fontFamily: '"Roboto Mono", monospace',
																			color: 'primary.main',
																		}}>
																		{fmtNum(netLocked)}
																	</Typography>
																</Box>
															</Stack>
														</Box>
													</>
												);
											})()}
										</Paper>
									</Grid>
								</Grid>
							)}

							{/* Data Tables */}
							<Grid container spacing={3}>
								<Grid item xs={12} md={6}>
									<DataTable
										title="Recent Accounts"
										columns={[
											{ key: 'account_id', label: 'ID' },
											{ key: 'owner', label: 'Owner' },
											{ key: 'currency', label: 'Currency' },
											{ key: 'balance', label: 'Balance' },
											{ key: 'active', label: 'Active' },
											{ key: 'transaction_hash', label: 'Tx Hash' },
										]}
										rows={filteredAccounts}
										filterableKeys={new Set(['account_id', 'owner', 'currency', 'active'])}
										onCellClick={(key, value, label) => addFilter(key, value, label, 'accounts')}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<DataTable
										title="Recent Envelopes"
										columns={[
											{ key: 'envelope_id', label: 'ID' },
											{ key: 'account_id', label: 'Account' },
											{ key: 'currency', label: 'Currency' },
											{ key: 'mode', label: 'Mode' },
											{ key: 'locked_amount', label: 'Locked' },
											{ key: 'total_allocated', label: 'Allocated' },
											{ key: 'total_disbursed', label: 'Disbursed' },
											{ key: 'available_balance', label: 'Available' },
											{ key: 'payment_count', label: 'Payments' },
										]}
										rows={filteredEnvelopes}
										filterableKeys={new Set(['currency', 'mode', 'account_id'])}
										onCellClick={(key, value, label) => addFilter(key, value, label, 'envelopes')}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<DataTable
										title="Recent Payments"
										columns={[
											{ key: 'payment_id', label: 'ID' },
											{ key: 'envelope_id', label: 'Envelope' },
											{ key: 'tx_type', label: 'Type' },
											{ key: 'amount', label: 'Amount' },
											{ key: 'status', label: 'Status' },
											{ key: 'blockchain_tx_hash', label: 'Tx Hash' },
										]}
										rows={filteredPayments}
										filterableKeys={new Set(['tx_type', 'status', 'envelope_id'])}
										onCellClick={(key, value, label) => addFilter(key, value, label, 'payments')}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<DataTable
										title="Recent Vouchers"
										columns={[
											{ key: 'voucher_id', label: 'ID' },
											{ key: 'envelope_id', label: 'Envelope' },
											{ key: 'recipient', label: 'Recipient' },
											{ key: 'amount', label: 'Amount' },
											{ key: 'is_redeemed', label: 'Redeemed' },
										]}
										rows={filteredVouchers}
										filterableKeys={new Set(['is_redeemed', 'envelope_id'])}
										onCellClick={(key, value, label) => addFilter(key, value, label, 'vouchers')}
									/>
								</Grid>

								{/* Account Transactions (MINT / BURN) */}
								<Grid item xs={12}>
									<DataTable
										title="Account Transactions (Mint / Burn)"
										columns={[
											{ key: 'id', label: 'ID' },
											{ key: 'account_id', label: 'Account' },
											{ key: 'tx_type', label: 'Type' },
											{ key: 'amount', label: 'Amount' },
											{ key: 'new_balance', label: 'New Balance' },
											{ key: 'currency', label: 'Currency' },
											{ key: 'transaction_hash', label: 'Tx Hash' },
											{ key: 'block_number', label: 'Block' },
											{ key: 'timestamp', label: 'Timestamp' },
										]}
										rows={filteredAccountTxns}
										filterableKeys={new Set(['tx_type', 'currency', 'account_id'])}
										onCellClick={(key, value, label) => addFilter(key, value, label, 'accountTxns')}
									/>
								</Grid>

								{/* Failures */}
								{data.failures && data.failures.length > 0 && (
									<Grid item xs={12}>
										<DataTable
											title="Failed Blockchain Operations"
											columns={[
												{ key: 'id', label: 'ID' },
												{ key: 'method', label: 'Method' },
												{ key: 'error_message', label: 'Error' },
												{ key: 'status', label: 'Status' },
												{ key: 'created_at', label: 'Created' },
											]}
											rows={data.failures}
										/>
									</Grid>
								)}
							</Grid>
						</>
					)}
				</Container>
			</Box>
		</Authenticated>
	);
};
